import { Link, useParams } from "react-router-dom";
import '../styles/EventsStyles/WebinarDetail.css';
import '../styles/EventsStyles/WebinarCard.css';
import EventsLayout from "../components/EventsSection/EventsLayout";
import PresenterAvatar from "../components/EventsSection/PresenterAvatar";
import RegistrationForm from "../components/EventsSection/RegistrationForm";
import StorageErrorNotice from "../components/EventsSection/StorageErrorNotice";
import { webinarRepository } from "../webinars/repository";
import { useRepositoryData } from "../webinars/useRepositoryData";
import { availability, isPublicWebinar, registrationClosesAt } from "../webinars/availability";
import { durationMinutes, formatDateTime, formatLongDate, formatTimeRange } from "../webinars/format";
import { presenterPhotoUrl } from "../webinars/photos";
import { formatPostDate, postsForWebinar } from "../news/posts";

const WebinarDetail = () => {
    const { slug = "" } = useParams();
    const { data, loading, error } = useRepositoryData(async () => {
        const webinar = await webinarRepository.getPublicWebinar(slug);
        if (!webinar) return { webinar, activeCount: 0 };
        const counts = await webinarRepository.countActiveRegistrations();
        return { webinar, activeCount: counts[webinar.id] ?? 0 };
    }, slug);

    if (loading && !data) {
        return (
            <EventsLayout>
                <div className="km-container ev-detail ev-missing"><p role="status">Loading webinar…</p></div>
            </EventsLayout>
        );
    }

    if (error) {
        return (
            <EventsLayout title="Webinar unavailable">
                <div className="km-container ev-detail ev-missing">
                    <StorageErrorNotice error={error} what="this webinar" />
                </div>
            </EventsLayout>
        );
    }

    const webinar = data?.webinar;
    if (!webinar || !isPublicWebinar(webinar)) {
        return (
            <EventsLayout title="Webinar not found">
                <div className="km-container ev-detail ev-missing">
                    <h1 className="km-title">Webinar not found</h1>
                    <p className="km-lead">This webinar doesn't exist or is no longer listed.</p>
                    <Link className="km-btn km-btn--primary" to="/events">See upcoming webinars</Link>
                </div>
            </EventsLayout>
        );
    }

    const state = availability(webinar, data.activeCount);
    const deadline = registrationClosesAt(webinar);
    const reading = postsForWebinar(webinar.slug);

    return (
        <EventsLayout title={webinar.title}>
            <div className="km-container ev-detail">
                <Link className="ev-back" to="/events">← All webinars</Link>
                <div className="ev-detail-grid">
                    <article className="km-card ev-detail-article">
                        {webinar.isDemo && (
                            <p className="ev-demo-note" role="note">
                                <strong>Demonstration event.</strong> This sample session shows how webinars will appear.
                                It is not a real Kole Medical event.
                            </p>
                        )}
                        <div className="ev-detail-chips">
                            <span className={`ev-chip ev-chip--${state.tone}`}>{state.label}</span>
                            {webinar.isDemo && <span className="ev-chip ev-chip--demo">Demo</span>}
                        </div>
                        <h1>{webinar.title}</h1>
                        <dl className="ev-facts">
                            <div><dt>Date</dt><dd>{formatLongDate(webinar.startAt)}</dd></div>
                            <div>
                                <dt>Time (SAST)</dt>
                                <dd>{formatTimeRange(webinar.startAt, webinar.endAt)} ({durationMinutes(webinar.startAt, webinar.endAt)} min)</dd>
                            </div>
                            <div><dt>Format</dt><dd>Online{webinar.meetingPlatform ? ` via ${webinar.meetingPlatform}` : ""}</dd></div>
                        </dl>
                        <p className="ev-summary">{webinar.description || webinar.summary}</p>
                        {webinar.topics.length > 0 && (
                            <>
                                <h2>Topics covered</h2>
                                <ul className="ev-learn">
                                    {webinar.topics.map(topic => <li key={topic}>{topic}</li>)}
                                </ul>
                            </>
                        )}
                        <div className="ev-presenter">
                            <PresenterAvatar name={webinar.presenter || "Kole Medical"} photoUrl={presenterPhotoUrl(webinar.presenter ? webinar.presenterPhoto : undefined)} size="lg" />
                            <span>
                                <small>Presented by</small>
                                <strong>{webinar.presenter || "Presenter to be announced"}</strong>
                                {webinar.presenterRole && <small>{webinar.presenterRole}</small>}
                            </span>
                        </div>
                        {reading.length > 0 && (
                            <div className="ev-reading">
                                <h2>Related reading</h2>
                                <ul>
                                    {reading.map(post => (
                                        <li key={post.slug}>
                                            <Link to={`/news/${post.slug}`}>{post.title}</Link>
                                            <small>{formatPostDate(post.publishedOn)}</small>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </article>

                    <aside className="km-card ev-detail-aside">
                        {state.canRegister ? (
                            <>
                                <h2>Register for this webinar</h2>
                                <p className="ev-aside-note">
                                    Takes about a minute. Fields marked * are required.
                                    Registration closes {formatDateTime(deadline)} SAST
                                    {webinar.capacity ? `, or when all ${webinar.capacity} seats are taken` : ""}.
                                </p>
                                <RegistrationForm webinar={webinar} />
                            </>
                        ) : (
                            <>
                                <h2>{state.label === "Past event" ? "This webinar has taken place" : state.label}</h2>
                                <p className="ev-aside-note">{state.closedReason}</p>
                                <Link className="km-btn km-btn--outline" to="/events">See other webinars</Link>
                            </>
                        )}
                    </aside>
                </div>
            </div>
        </EventsLayout>
    );
};

export default WebinarDetail;
